import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Ticket, Users, Trophy, Coins, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../context/AuthContext";
import { useLeague } from "../../context/LeagueContext";
import { fmtDate, fmtOdds, fmtTokens } from "../../lib/format";
import CouponReceipt from "../../components/CouponReceipt";

const card = {
  hidden: { opacity: 0, y: 20 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Overview() {
  const { currentLeague } = useLeague();
  const [data, setData] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponTab, setCouponTab] = useState("active");
  const [receipt, setReceipt] = useState(null);

  const load = () => {
    if (currentLeague) {
      api.get(`/leagues/${currentLeague.id}/results`).then((r) => setData(r.data)).catch(() => {});
      api.get(`/leagues/${currentLeague.id}/coupons?status=${couponTab}`).then((r) => setCoupons(r.data)).catch(() => {});
    }
  };

  useEffect(() => { load(); }, [currentLeague, couponTab]);

  const stats = data
    ? [
        { label: "Spelare", value: data.totals.players, icon: Users, testid: "stat-players" },
        { label: "Aktiva kuponger", value: data.totals.coupons_active, icon: Ticket, testid: "stat-active-coupons" },
        { label: "Vunna kuponger", value: data.totals.coupons_won, icon: Trophy, testid: "stat-won-coupons" },
        { label: "Satsade tokens", value: fmtTokens(data.totals.tokens_wagered), icon: Coins, testid: "stat-tokens-wagered" },
      ]
    : [];

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-2">Översikt</h1>
      <p className="text-sm text-slate-500 mb-10">Snabbstatus för {currentLeague?.name}.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            variants={card}
            initial="hidden"
            animate="show"
            custom={i}
            className="bg-white border border-slate-200 rounded-2xl p-6"
            data-testid={s.testid}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">{s.label}</p>
              <s.icon size={18} className="text-blue-600" />
            </div>
            <p className="font-display font-bold text-3xl">{s.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Coupon history */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-bold text-xl sm:text-2xl">Kuponghistorik</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setCouponTab("active")}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                couponTab === "active" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
              data-testid="coupon-tab-active"
            >
              Aktiva
            </button>
            <button
              onClick={() => setCouponTab("settled")}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                couponTab === "settled" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
              data-testid="coupon-tab-settled"
            >
              Avgjorda
            </button>
          </div>
        </div>

        <div className="space-y-4 max-h-[600px] overflow-y-auto">
          {coupons.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">
              {couponTab === "active" ? "Inga aktiva kuponger." : "Inga avgjorda kuponger ännu."}
            </p>
          )}
          {coupons.map((c, i) => (
            <motion.button
              key={c.id}
              type="button"
              onClick={() => setReceipt(c)}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="w-full text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-shadow"
              data-testid={`coupon-history-${c.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                    <Ticket size={18} />
                  </div>
                  <div>
                    <p className="font-display font-semibold">{c.player_name}</p>
                    <p className="text-[11px] text-slate-500">Lagd {fmtDate(c.created_at)}</p>
                  </div>
                </div>
                <span className={`text-xs font-semibold border px-3 py-1 rounded-full ${
                  c.status === "won" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  c.status === "lost" ? "bg-red-50 text-red-700 border-red-200" :
                  "bg-blue-50 text-blue-700 border-blue-200"
                }`}>
                  {c.status === "won" ? "Vunnen" : c.status === "lost" ? "Förlorad" : "Aktiv"}
                </span>
              </div>

              <div className="space-y-1.5 mb-3">
                {c.legs.map((leg, j) => {
                  const betType = leg.market_label || (leg.pick === "over" || leg.pick === "under" ? "Över/Under" : "1X2");
                  const displayPick = leg.label || (leg.pick === "over" ? "Över" : leg.pick === "under" ? "Under" : leg.pick);
                  return (
                    <div key={j} className="text-xs text-slate-600 flex justify-between gap-4">
                      <span className="flex-1">{leg.home_team} – {leg.away_team} <span className="text-slate-400">({betType})</span></span>
                      <span className="font-mono2 whitespace-nowrap">{displayPick} · {fmtOdds(leg.odds)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-5 text-xs pt-3 border-t border-slate-100">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Insats</p>
                  <p className="font-mono2 font-semibold">{fmtTokens(c.stake)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Odds</p>
                  <p className="font-mono2 font-semibold">{fmtOdds(c.total_odds)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">
                    {c.status === "active" ? "Möjlig vinst" : "Utbetalning"}
                  </p>
                  <p className={`font-mono2 font-bold ${c.status === "won" ? "text-emerald-600" : c.status === "lost" ? "text-red-500" : "text-blue-600"}`}>
                    {fmtTokens(c.status === "active" ? c.potential_return : c.payout)}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <CouponReceipt coupon={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
